import React, { useMemo } from 'react';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';
import type { BuildHubCollaborator } from '../../types';
import { BuildHubRoleEnum } from 'generated/graphql';
import { CollaboratorRow } from '@modules/build-hub/components/build-hub-admin/CollaboratorRow';
import { buildFormDataForCollaboratorUpdate } from '@modules/build-hub/utils/build-collaborator-update-data';
import { useBuildHubCollaboratorUpdate } from '@modules/build-hub/hooks/use-build-hub-collaborator-update';
import { buildFormDataForCollaboratorRemove } from '@modules/build-hub/utils/build-collaborators-remove-data';
import { useBuildHubCollaboratorRemove } from '@modules/build-hub/hooks/use-build-hub-collaborator-remove';
import { useBuildHubCollaboratorResend } from '@modules/build-hub/hooks/use-build-hub-collaborator-resend';
import { toast } from 'sonner';
import { useUser } from '@modules/user';

const onRemoveSuccess = () =>
    toast.success('Collaborator removed successfully.', {
        action: { label: 'Dismiss', onClick: () => toast.dismiss() },
    });

const onUpdateSuccess = () =>
    toast.success('Collaborator role updated successfully.', {
        action: { label: 'Dismiss', onClick: () => toast.dismiss() },
    });

const onReinviteSuccess = () =>
    toast.success('Invitation send.', {
        action: { label: 'Dismiss', onClick: () => toast.dismiss() },
    });

export const ExistingCollaborators = () => {
    const { buildHub } = useBuildHubData();
    const user = useUser();

    const { removeCollaborator } = useBuildHubCollaboratorRemove({
        onSuccess: onRemoveSuccess,
    });

    const { updateCollaborator } = useBuildHubCollaboratorUpdate({
        onSuccess: onUpdateSuccess,
    });

    const { resendInvite } = useBuildHubCollaboratorResend({
        onSuccess: onReinviteSuccess,
    });

    // Check if the current user can manage team members
    // Editors and Admins can manage roles, but Editors have restrictions
    const canManageTeam = useMemo(() => {
        return buildHub.userPermissions.canEdit;
    }, [buildHub.userPermissions.canEdit]);

    // Calculate available roles based on current user's role
    // Users cannot assign roles higher than or equal to their own
    const availableRoles = useMemo(() => {
        const currentUserRole = buildHub.currentUserRole;
        const isOwner = String(buildHub.ownerId) === String(user.ID);

        if (isOwner) {
            return [
                BuildHubRoleEnum.Admin,
                BuildHubRoleEnum.Editor,
                BuildHubRoleEnum.Viewer,
            ];
        }

        // If no role, return empty array (shouldn't happen, but safe default)
        if (!currentUserRole) {
            return [];
        }

        // Admins can assign Editor and Viewer roles (not Admin)
        if (currentUserRole === BuildHubRoleEnum.Admin) {
            return [BuildHubRoleEnum.Editor, BuildHubRoleEnum.Viewer];
        }

        // Editors can only assign Viewer role (not Editor or Admin)
        if (currentUserRole === BuildHubRoleEnum.Editor) {
            return [BuildHubRoleEnum.Viewer];
        }

        // Viewers cannot assign any roles
        return [];
    }, [buildHub.currentUserRole, buildHub.ownerId, user.ID]);

    const handleRoleChange = (
        collaboratorId: string,
        newRole: BuildHubRoleEnum
    ) => {
        try {
            const formData = buildFormDataForCollaboratorUpdate({
                buildHubId: buildHub.id,
                collaboratorId: collaboratorId,
                role: newRole,
            });

            updateCollaborator(formData);
        } catch (error) {
            console.log(error);
        }
    };

    const handleCollaboratorReinvite = (collaboratorID: string) => {
        try {
            const formData = new FormData();
            formData.append('collaboratorID', collaboratorID);
            resendInvite(formData);
        } catch (error) {
            console.log(error);
        }
    };

    const handleCollaboratorRemove = (collaboratorId: string) => {
        try {
            const formData = buildFormDataForCollaboratorRemove({
                buildHubId: buildHub.id,
                collaboratorId: collaboratorId,
            });

            removeCollaborator(formData);
        } catch (error) {
            console.log(error);
        }
    };

    const canReinvite = (collaborator: BuildHubCollaborator) => {
        // Can't reinvite if already onboarded or if they're the owner
        return (
            !collaborator.onBoarded &&
            String(buildHub.ownerId) !== collaborator.memberID
        );
    };

    const canRemove = (collaborator: BuildHubCollaborator) => {
        // Can't remove yourself or the owner
        if (
            collaborator.memberID === user.ID.toString() ||
            String(buildHub.ownerId) === collaborator.memberID
        ) {
            return false;
        }

        // Admins (and Owners) can remove any collaborator
        if (canManageTeam) {
            return true;
        }

        // Editors can only remove collaborators they invited
        if (
            buildHub.userPermissions.canEdit &&
            collaborator.invitedByMemberID === user.ID
        ) {
            return true;
        }

        // Viewers and Editors who didn't invite this user cannot remove
        return false;
    };

    /**
     * Returns the roles that can be displayed/selected for a collaborator.
     * For the current user's own role, only show their current role (read-only).
     * For other collaborators, show the roles that the current user can assign.
     */
    const getAvailableRolesForCollaborator = (
        collaborator: BuildHubCollaborator
    ): BuildHubRoleEnum[] => {
        const isCurrentUser = collaborator.memberID === user.ID.toString();
        const currentRole = collaborator.role as BuildHubRoleEnum;

        if (isCurrentUser) {
            return [currentRole];
        }

        // Include the collaborator's current role for proper Select display
        return availableRoles.includes(currentRole)
            ? availableRoles
            : [currentRole, ...availableRoles];
    };

    // Sort collaborators by sortOrder
    const sortedCollaborators = useMemo(() => {
        return [...(buildHub.collaborators || [])].sort(
            (a, b) => a.sortOrder - b.sortOrder
        );
    }, [buildHub.collaborators]);

    return (
        <div>
            <h2 className="mb-1 block text-base font-medium text-gray-900">
                Who has access
            </h2>
            <p className="mb-4 text-sm text-gray-600">
                Manage who has access to this project.
            </p>
            {sortedCollaborators.length > 0 && (
                <div className="space-y-3">
                    {sortedCollaborators.map(
                        (collaborator: BuildHubCollaborator) => (
                            <CollaboratorRow
                                key={collaborator.email}
                                collaborator={collaborator}
                                ownerId={buildHub.ownerId}
                                onRoleChange={
                                    canManageTeam
                                        ? (newRole) =>
                                              handleRoleChange(
                                                  collaborator.id,
                                                  newRole
                                              )
                                        : undefined
                                }
                                onRemove={
                                    canRemove(collaborator)
                                        ? handleCollaboratorRemove
                                        : undefined
                                }
                                onReInvite={
                                    canReinvite(collaborator)
                                        ? handleCollaboratorReinvite
                                        : undefined
                                }
                                availableRoles={getAvailableRolesForCollaborator(
                                    collaborator
                                )}
                            />
                        )
                    )}
                </div>
            )}
        </div>
    );
};
